import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
	ArticleFragment,
	GetArticleByUrlDocument,
	GetArticleByUrlQuery,
	GetArticleByUrlQueryVariables,
	LinkedListFragment,
	LinkedTitleFragment,
} from '@/pages/graphql/queries/GetArticleByUrl.query';

import type { WebLocale } from '@/enums/web-locale';

import { getArticleBackdrop } from '@/helpers/editorial-helper';
import { AssertByTypename, toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { toastError } from '@/helpers/toast/toast-helper';

import { ArticleType, BackdropProfile, ImageFormat } from '@/@types/graphql-types';
import { FilterCollection } from '@/stores/filter.store';
import { UserSettings } from '@/stores/user.store';
import { timestamp } from '@/helpers/date-helper';

const Filter = namespace('filter');
const Language = namespace('language');
const User = namespace('user');

export interface Provision {
	author: ArticleFragment['author'];
	article: ArticleFragment['content'];
	mainObject: LinkedTitleFragment | LinkedListFragment | null;
	publishDate: string;
}

export interface ListProvision extends Provision {
	mainObject: LinkedListFragment;
}

export interface NewsProvision extends Provision {
	mainObject: LinkedTitleFragment;
}

@Component({
	name: 'UseArticle',
	apollo: {
		article: {
			query: GetArticleByUrlDocument,
			variables(): GetArticleByUrlQueryVariables {
				return this.queryVariables;
			},
			update(data: GetArticleByUrlQuery) {
				const article = data.urlV2.node;
				this.$store.dispatch('meta/setArticleJsonLD', article);

				return article;
			},
			skip() {
				return this.$route.name !== 'app.editorial.article';
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Article]:',
					{ error: error, where: 'Component: useArticle' },
					'error'
				);
				process.client ? this.showErrorToast() : null;
			},
		},
	},
})
export default class UseArticle extends Vue {
	@Filter.Getter('editorial') editorialFilters: FilterCollection;

	@Language.Getter language: string;
	@Language.Getter country: string;
	@Language.State webLocale: WebLocale;

	@User.State settings: UserSettings;
	@User.Getter isHideSeenDislikedTitlesEnabled: boolean;

	article: ArticleFragment = {
		author: {
			id: '',
			content: {
				bio: '',
				clearName: '',
				imageUrl: '',
				fullPath: '',
				alumniOf: [],
				brand: '',
				languagesSpoken: [],
				occupation: [],
			},
		},
		content: {
			articleType: ArticleType.NewsArticle,
			title: '',
			subtitle: '',
			paragraph1: '',
			paragraph2: '',
			synopsis: '',
			whereToWatch: '',
			whatToKnow: '',
			productionNews: '',
			createdAt: '',
			updatedAt: '',
			mainImageEntityBackdrop: { backdropUrl: '' },
		},
		mainObjects: [],
		secondaryObjects: [],
		supportingObjects: [],
	};

	get queryVariables(): GetArticleByUrlQueryVariables {
		return {
			fullPath: this.$route.fullPath,
			country: this.country,
			language: this.language,
			first: 20,
			profile: BackdropProfile.S1440,
			format: ImageFormat.Png,
			filter: toGraphqlFilters(
				this.editorialFilters,
				this.editorialFilters.providers,
				this.isHideSeenDislikedTitlesEnabled
			),
		};
	}

	get isLoading() {
		return this.$apollo.queries.article.loading;
	}

	get mainObject(): Provision['mainObject'] {
		const mainObject = this.article.mainObjects?.[0];
		if (mainObject == null) return null;

		type Node = typeof mainObject.node;
		return mainObject.node as AssertByTypename<Node, 'Movie' | 'Show' | 'GenericTitleList'>;
	}

	get isList() {
		return this.mainObject?.__typename === 'GenericTitleList';
	}

	get publishDate() {
		if (!this.article.content?.createdAt) return '';

		return timestamp(
			new Date(this.article.content.createdAt),
			{ year: 'numeric', month: 'long', day: 'numeric' },
			this.country,
			this.language
		).date;
	}

	get isUpdated() {
		if (!this.article.content?.updatedAt) return false;

		const publishedDate = new Date(this.article.content.createdAt);
		const updatedDate = new Date(this.article.content.updatedAt);
		// isUpdated only true for different days
		return publishedDate.setHours(0, 0, 0, 0) !== updatedDate.setHours(0, 0, 0, 0);
	}

	get updatedDate() {
		if (!this.article.content?.updatedAt) return '';

		return timestamp(
			new Date(this.article.content.updatedAt),
			{ year: 'numeric', month: 'long', day: 'numeric' },
			this.country,
			this.language
		).date;
	}

	get backdrop() {
		return !!this.mainImageEntityBackdrop
			? `https://images.justwatch.com${this.mainImageEntityBackdrop}`
			: getArticleBackdrop(this.mainObject, this.$store.state);
	}

	get mainImageEntityBackdrop() {
		return !!this.article?.content?.mainImageEntityBackdrop?.backdropUrl
			? this.article.content.mainImageEntityBackdrop.backdropUrl
			: '';
	}

	showErrorToast() {
		toastError({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}

	onLoadNext({ target }: { target?: { complete: (len: number) => void } }) {
		if (!target || this.mainObject?.__typename !== 'GenericTitleList') return;

		this.$apollo.queries.article
			.fetchMore({
				variables: {
					after: this.mainObject.titles.pageInfo.endCursor,
				},
				updateQuery(
					previousResult: GetArticleByUrlQuery,
					{ fetchMoreResult }: { fetchMoreResult?: GetArticleByUrlQuery }
				) {
					if (!fetchMoreResult) return previousResult;

					const article = previousResult.urlV2.node as ArticleFragment;
					const mainObject = article.mainObjects[0].node as ListProvision['mainObject'];

					const fetchedTitles = (
						(fetchMoreResult.urlV2.node as ArticleFragment).mainObjects[0]
							.node as ListProvision['mainObject']
					).titles;

					if (mainObject.titles.edges == null || fetchedTitles.edges == null) return previousResult;

					mainObject.titles.edges.push(...fetchedTitles.edges);
					mainObject.titles.pageInfo = fetchedTitles.pageInfo;

					return previousResult;
				},
			})
			.catch(e => {
				captureMessageForSentry(
					'[GraphQL List Article Page Load More Titles Issue]:',
					{ error: e, where: 'Origin: editorial-article-provider, method: onLoadNext' },
					'error'
				);
			})
			.finally(() =>
				target.complete(((this.mainObject as ListProvision['mainObject']).titles.edges ?? []).length)
			);
	}

	render() {
		if (this.$scopedSlots.default) {
			return this.$scopedSlots.default({
				isLoading: this.isLoading,

				author: this.article.author,
				article: this.article.content,
				mainObject: this.mainObject,
				publishDate: this.publishDate,
				backdrop: this.backdrop,
				isList: this.isList,
				isNews: !this.isList,
				isUpdated: this.isUpdated,
				updatedDate: this.updatedDate,

				listEvents: {
					'load-next': this.onLoadNext,
				},
			} as Provision);
		}

		return this.$slots[0];
	}
}
